import { useMemo, useRef, useState } from "react"
import { AmendsEditor } from "./amends-editor"
import { buildEditableAmendsMacroValue, normalizeAmendsMacroValue } from "../lib/macro-amends-utils"

/**
 * Wraps AmendsEditor to display macro tokens as visual chips in edit mode.
 * On save, chip HTML is stripped back to $TOKEN$ form via normalizeAmendsMacroValue.
 * The parent receives plain string values (not chip HTML) through onSave/onChange.
 */
export function AmendsEditorWithMacros({ macroMap, value, onChange, onSave, editMode, ...props }) {
    const initialChipValue = useMemo(buildInitialChipValue, [value, macroMap])
    const [editorValue, setEditorValue] = useState(initialChipValue)
    const lastRawValueRef = useRef(value)
    const lastMacroMapRef = useRef(macroMap)

    // If the raw value changes externally (e.g. cancel/reload), rebuild chip HTML
    if (value !== lastRawValueRef.current) {
        lastRawValueRef.current = value
        lastMacroMapRef.current = macroMap
        setEditorValue(buildEditableAmendsMacroValue(value, macroMap) ?? value)
    } else if (macroMap !== lastMacroMapRef.current) {
        // macroMap arrived after initial render (async load) — rebuild chips from current raw value
        lastMacroMapRef.current = macroMap
        setEditorValue(buildEditableAmendsMacroValue(lastRawValueRef.current, macroMap) ?? lastRawValueRef.current)
    }

    // In readonly mode, pass raw value so MacroExpander can expand tokens to content.
    // In edit mode, pass chip HTML so tokens are visible as styled chips.
    const displayValue = editMode ? editorValue : value

    return (
        <AmendsEditor {...props} editMode={editMode} value={displayValue} onChange={handleChange} onSave={handleSave} />
    )

    function buildInitialChipValue() {
        return buildEditableAmendsMacroValue(value, macroMap) ?? value
    }

    function handleChange(nextEditorValue) {
        setEditorValue(nextEditorValue)
        const normalized = normalizeAmendsMacroValue(nextEditorValue)
        lastRawValueRef.current = normalized
        onChange?.(normalized)
    }

    function handleSave() {
        const normalized = normalizeAmendsMacroValue(editorValue)
        lastRawValueRef.current = normalized
        onChange?.(normalized)
        onSave?.()
    }
}
